import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { themes } from '@vimeo/iris/themes';
import { TeamUserPermissionLevel, User } from '@vimeo/viewer';
import { ViewerContext } from '@vimeo/viewer';

import {
  BottomSwatch,
  FullScreen,
  PaddedWrapper,
  TopSwatch,
  VideoGrid,
  GridHeaderWrapper,
  StyledSearch,
} from './GridStyled';
import CustomGridHeader from '../components/AlbumHeader/CustomGridHeader';
import EmptyAlbum from '../components/EmptyAlbum';
import TeamMenu from '../components/LiveNav/TeamMenu';
import LoadingSpinner from '../components/LoadingSpinner';
import PaginationFooter from '../components/PaginationLayout/PaginationFooter';
import PaginationHeader from '../components/PaginationLayout/PaginationHeader';
import SearchGrid from '../components/Search/SearchGrid';
import { useSearchState } from '../components/SearchContext';
import VideoCard from '../components/VideoCard';
import VideoCardPlaceholder from '../components/VideoCardPlaceholder';
import { EmbedShowcaseContext } from '../context/useEmbedShowcase';
import { AlbumLayouts, AlbumThemes, ClipDataObject, Pictures, UserPlaceholder } from '../types';
import { LAYOUT_GRID } from '../utils/constants';
import { getDefaultThumbnailUri, getLayoutPageSize } from '../utils/helpers';

interface Props {
  albumId: number;
  albumHash?: string;
  albumIsEmpty: boolean;
  customLogo: Pictures;
  webCustomLogo: boolean;
  description: string;
  isOwnerOrAdmin: boolean;
  layout: AlbumLayouts;
  name: string;
  theme: AlbumThemes;
  videos: ClipDataObject[];
  totalVideos: number;
  openShare: VoidFunction;
  isUpdating: boolean;
  page: number;
  setPage: (n: number) => void;
  curUser?: User;
  isOffsite?: boolean;
  allowDownloads: boolean;
  setDownloadClipId?: (id: number) => void;
  openDownloadModal?: () => void;
  allowShare: boolean;
  albumOwnerId?: number;
  teamRole?: string;
  pictures: Pictures | Pictures[];
  hasChosenThumbnail: boolean;
}

/**
 * Container to display playback layout
 */
export default function Grid({
  albumId,
  albumHash,
  albumIsEmpty,
  customLogo,
  webCustomLogo,
  description,
  isOwnerOrAdmin,
  name,
  theme,
  openShare,
  videos = [],
  totalVideos,
  page,
  setPage,
  isUpdating,
  curUser,
  isOffsite = false,
  allowDownloads,
  setDownloadClipId,
  openDownloadModal,
  allowShare,
  albumOwnerId,
  teamRole,
  pictures,
  hasChosenThumbnail,
}: Props) {
  const openShareWithTracking = () => {
    openShare();
    FA.trackClick({
      container: 'album_player',
      component: 'album',
      keyword: 'share',
    });
    if (teamRole) {
      FA.trackClick({
        container: 'team_home_page',
        component: 'share_modal',
        keyword: 'open_share_' + teamRole.toLowerCase(),
      });
    }
  };

  const defaultThumbnail = useMemo(
    () => getDefaultThumbnailUri(hasChosenThumbnail ? pictures : []),
    [pictures, hasChosenThumbnail],
  );

  const viewer = useContext(ViewerContext);
  const embedShowcaseData = useContext(EmbedShowcaseContext);
  if (!viewer) {
    return null;
  }
  const { vimeoHttpsUrl } = viewer;
  const onSearchPlay = (id: number, isStock?: boolean) => {
    const searchResultUrl = isStock
      ? `${vimeoHttpsUrl}/${id}`
      : `${vimeoHttpsUrl}/showcase/${albumId}/video/${id}`;
    if (embedShowcaseData?.isShowcaseEmbeded) {
      window.open(searchResultUrl, '_blank');
    } else {
      window.location.href = searchResultUrl;
    }
  };
  const isImpersonatingViewerOrContrib =
    viewer?.user?.teamUserPermissionLevel &&
    (viewer.user.teamUserPermissionLevel === TeamUserPermissionLevel.Contributor ||
      viewer.user.teamUserPermissionLevel === TeamUserPermissionLevel.Viewer);

  const { isSearching, isLoading, hasErrored, results, searchPage, searchTotal } = useSearchState();

  return (
    <PaddedWrapper>
      <TopSwatch thumbnail={defaultThumbnail} theme={theme} isSearching={isSearching}>
        <GridHeaderWrapper isSearching={isSearching}>
          <CustomGridHeader
            isSearching={isSearching}
            customLogo={customLogo}
            webCustomLogo={webCustomLogo}
            description={description}
            name={name}
          />
          <div>
            {!isImpersonatingViewerOrContrib && (
              <StyledSearch autofocus={false} teamRole={teamRole} />
            )}
            {albumOwnerId && teamRole && !embedShowcaseData?.isShowcaseEmbeded && (
              <TeamHomeWrapper>
                <TeamMenu
                  ownerId={albumOwnerId}
                  userRole={teamRole}
                  hrefColor={themes[theme === 'dark' ? 'dark' : 'light'].formats.soft}
                />
              </TeamHomeWrapper>
            )}
          </div>
        </GridHeaderWrapper>
      </TopSwatch>
      {!albumIsEmpty ? (
        <BottomSwatch>
          {isSearching ? null : (
            <PaginationHeader
              theme={theme}
              currentPage={page}
              totalItems={totalVideos}
              onShare={openShareWithTracking}
              allowShare={allowShare}
              pageSize={getLayoutPageSize(LAYOUT_GRID)}
            />
          )}
          {isSearching ? (
            <SearchGrid
              results={results}
              page={searchPage}
              totalVideos={searchTotal}
              isLoading={isLoading}
              hasErrored={hasErrored}
              onPlay={(id: number, isStock?: boolean) => {
                onSearchPlay(id, isStock);
              }}
            />
          ) : (
            <VideoGrid>
              {videos.map((video, i) => {
                switch (video.displayType) {
                  case 'CLIP_PLACEHOLDER':
                    const user: UserPlaceholder = video.user;
                    return (
                      <VideoCardPlaceholder
                        video={video}
                        user={user}
                        theme={theme}
                        allowDownloads={allowDownloads}
                      />
                    );
                  case 'CLIP':
                  default:
                    return (
                      <VideoCard
                        key={video.uri}
                        albumId={albumId}
                        albumHash={albumHash}
                        theme={theme}
                        video={video}
                        curUser={curUser}
                        isOffsite={isOffsite}
                        allowDownloads={allowDownloads}
                        setDownloadClipId={setDownloadClipId}
                        openDownloadModal={openDownloadModal}
                        trackClipClick={() =>
                          FA.trackClick({
                            container: 'showcase',
                            component: 'grid_video_row',
                            keyword: i,
                          })
                        }
                      />
                    );
                }
              })}
            </VideoGrid>
          )}
          {!isSearching && (
            <PaginationFooter
              theme={theme}
              currentPage={page}
              totalItems={totalVideos}
              onSetPage={setPage}
              pageSize={getLayoutPageSize(LAYOUT_GRID)}
            />
          )}
        </BottomSwatch>
      ) : (
        !isUpdating && <EmptyAlbum isOwnerOrAdmin={isOwnerOrAdmin} theme={theme} />
      )}
      {isUpdating && (
        <FullScreen>
          <LoadingSpinner />
        </FullScreen>
      )}
    </PaddedWrapper>
  );
}

const TeamHomeWrapper = styled.div`
  position: absolute;
  right: 200px;
  top: 23px;
`;
